import React, { useEffect, useMemo, useRef, useState, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "../styles/NewsPage.css";
import { getUserLanguage } from "../utils.js";
import { fetchNews, fetchCategories, prefetchNextPage } from "../services/newsService.js";

const PAGE_SIZE = 12;
const PREFETCH_SIZE = 6;
const ALL = "__all";
const IMAGE_SIZES = {
  mobile: 360,
  tablet: 480,
  desktop: 640
};

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const normalizeLang = (lang) => {
  if (!lang) return [];
  const base = lang.split("-")[0];
  return lang.includes("-") ? [lang, base] : [lang];
};

// Memoized NewsItem component for better performance
const NewsItem = memo(({ item, readMoreText }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const handleImageLoad = useCallback(() => setImageLoaded(true), []);
  const handleImageError = useCallback(() => {
    setImageError(true);
    setImageLoaded(true);
  }, []);

    const { formattedDate, isoDate } = useMemo(() => {
      const d = toDate(item.date);
      if (!d) return { formattedDate: "", isoDate: undefined };
      return {
        formattedDate: d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }),
        isoDate: d.toISOString(),
      };
  }, [item.date]);

  return (
    <article className="news-item" itemScope itemType="https://schema.org/NewsArticle">
      <div className="news-item-image-container">
        {!imageLoaded && <div className="image-skeleton" />}
        <img
          className={`news-item-image ${imageLoaded ? 'loaded' : ''}`}
          loading="lazy"
          width={IMAGE_SIZES.mobile}
          height={Math.round(IMAGE_SIZES.mobile * 0.56)}
          src={imageError ? '/placeholder-news.jpg' : (item.imageThumb || item.image)}
          srcSet={!imageError && item.image ? `
            ${item.imageThumb || item.image} 360w,
            ${item.image} 640w
          ` : undefined}
          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
          alt={item.title}
          onLoad={handleImageLoad}
          onError={handleImageError}
          itemProp="image"
        />
      </div>
      <div className="news-item-content">
        <h3 itemProp="headline">{item.title}</h3>
        <div className="news-item-meta">
          {isoDate && (
            <time className="news-item-date" dateTime={isoDate} itemProp="datePublished">
              {formattedDate}
            </time>
          )}

          {item.category && item.category !== ALL && (
            <span className="news-item-category">{item.category}</span>
          )}
        </div>
        {item.excerpt && (
          <p className="news-item-excerpt" itemProp="description">
            {item.excerpt}
          </p>
        )}
        <Link 
          to={`/news/${item.id}`} 
          className="read-more"
          aria-label={`Read more about ${item.title}`}
          itemProp="url"
        >
          {readMoreText}
        </Link>
      </div>
    </article>
  );
});

NewsItem.displayName = 'NewsItem';

// Skeleton loader component
const NewsItemSkeleton = memo(() => (
  <div className="news-item skeleton">
    <div className="skeleton-image" />
    <div className="news-item-content">
      <div className="skeleton-title" />
      <div className="skeleton-date" />
      <div className="skeleton-text" />
      <div className="skeleton-button" />
    </div>
  </div>
));

NewsItemSkeleton.displayName = 'NewsItemSkeleton';

const NewsPage = () => {
  const { t, i18n } = useTranslation();
  
  // State management
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([ALL]);
  const [selectedCategory, setSelectedCategory] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [emptyHint, setEmptyHint] = useState(null);
  
  // Refs for pagination and cleanup
  const lastDocRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const abortControllerRef = useRef(null);
  const observerRef = useRef(null);
  const sentinelRef = useRef(null);
  
  // Memoized values
  const allLabel = useMemo(() => t("newsBanner.all"), [t]);
  const readMoreText = useMemo(() => t("newsBanner.readMore"), [t]);
  const userLanguage = useMemo(() => getUserLanguage(), [i18n.language]);
  const langs = useMemo(() => normalizeLang(userLanguage), [userLanguage]);
  
  // Optimized fetch with caching via newsService
  const fetchFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEmptyHint(null);
    setItems([]);
    setHasMore(true);
    lastDocRef.current = null;
    
    try {
      // Fetch news using cached service
      const result = await fetchNews({
        language: userLanguage,
        category: selectedCategory === ALL ? null : selectedCategory,
        cursor: null,
        pageSize: PAGE_SIZE,
        useCache: true,
      });
      
      if (result.items.length > 0) {
        setItems(result.items);
        setHasMore(result.hasMore);
        lastDocRef.current = result.lastDoc;
        
        // Fetch and set categories
        const cats = await fetchCategories(userLanguage);
        setCategories(cats);
        
        // Prefetch next page in background
        if (result.hasMore && result.lastDoc) {
          prefetchNextPage({
            language: userLanguage,
            category: selectedCategory === ALL ? null : selectedCategory,
            lastDoc: result.lastDoc,
            pageSize: PREFETCH_SIZE,
          });
        }
      } else {
        setItems([]);
        setHasMore(false);
        setEmptyHint(
          `No articles found for language "${userLanguage}"${
            selectedCategory !== ALL ? ` and category "${selectedCategory}"` : ""
          }.`
        );
      }
    } catch (err) {
      setError("Failed to load articles. Please try again later.");
      console.error("Error loading news:", err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [userLanguage, selectedCategory]);
  
  // Load more with caching
  const fetchMoreItems = useCallback(async () => {
    if (!hasMore || loadingMore || loading || !lastDocRef.current) return;
    
    setLoadingMore(true);
    setError(null);
    
    try {
      const result = await fetchNews({
        language: userLanguage,
        category: selectedCategory === ALL ? null : selectedCategory,
        cursor: lastDocRef.current,
        pageSize: PAGE_SIZE,
        useCache: true,
      });
      
      if (result.items.length > 0) {
        lastDocRef.current = result.lastDoc;
        setItems(prev => [...prev, ...result.items]);
        setHasMore(result.hasMore);
        
        // Prefetch next batch
        if (result.hasMore && result.lastDoc) {
          prefetchNextPage({
            language: userLanguage,
            category: selectedCategory === ALL ? null : selectedCategory,
            lastDoc: result.lastDoc,
            pageSize: PREFETCH_SIZE,
          });
        }
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setError("Failed to load more articles.");
      console.error("Error loading more news:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading, userLanguage, selectedCategory]);
  
  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };
    
    observerRef.current = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
        fetchMoreItems();
      }
    }, options);
    
    observerRef.current.observe(sentinelRef.current);
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, fetchMoreItems]);
  
  // Initial load and category/language change
  useEffect(() => {
    fetchFirstPage();
    
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [fetchFirstPage]);
  
  return (
    <section className="news-page" role="main" aria-label="News Articles">
      <header className="news-header">
        <h1>{t("newsBanner.header")}</h1>
      </header>
      
      {/* Error and hint messages */}
      {error && (
        <div className="alert alert-error" role="alert" aria-live="assertive">
          <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}
      
      {emptyHint && !error && (
        <div className="alert alert-info" role="status" aria-live="polite">
          <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span>{emptyHint}</span>
        </div>
      )}
      
      {/* Category filters */}
      <nav className="news-categories" role="navigation" aria-label="News categories">
        {categories.map(cat => (
          <button
            key={cat}
            className={`category-button ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
            aria-pressed={selectedCategory === cat}
            aria-label={`Filter by ${cat === ALL ? 'all categories' : cat}`}
          >
            {cat === ALL ? allLabel : cat}
          </button>
        ))}
      </nav>
      
      {/* News grid */}
      <div className="news-grid" role="feed" aria-busy={loading}>
        {loading && !items.length && (
          [...Array(6)].map((_, i) => <NewsItemSkeleton key={`skeleton-${i}`} />)
        )}
        
        {!loading && items.length === 0 && !error && (
          <div className="empty-state">
            <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p>No articles available at the moment.</p>
          </div>
        )}
        
        {items.map(item => (
          <NewsItem key={item.id} item={item} readMoreText={readMoreText} />
        ))}
        
        {loadingMore && (
          [...Array(3)].map((_, i) => <NewsItemSkeleton key={`loading-${i}`} />)
        )}
      </div>
      
      {/* Infinite scroll sentinel */}
      {!loading && hasMore && (
        <div ref={sentinelRef} className="load-more-sentinel" aria-hidden="true">
          <button 
            className="load-more" 
            onClick={fetchMoreItems}
            disabled={loadingMore}
            aria-label="Load more articles"
          >
            {loadingMore ? (
              <>
                <span className="spinner" aria-hidden="true" />
                {t("common.loading")}
              </>
            ) : (
              t("common.loadMore") || "Load more"
            )}
          </button>
        </div>
      )}
    </section>
  );
};

export default NewsPage;
